import { Box, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { keyframes } from "@mui/system";

interface AILoadingMessagesProps {
    isActive: boolean;
    onComplete?: () => void;
}

const AI_MESSAGES = [
    "Analyzing clinical notes...",
    "Identifying key patterns...",
    "Extracting SMART goals...",
    "Finalizing recommendations...",
];

const MESSAGE_DURATION = 1000; // 1 second per message
const TOTAL_DURATION = AI_MESSAGES.length * MESSAGE_DURATION; // Total time to show all messages

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;


const AILoadingMessages = ({ isActive, onComplete }: AILoadingMessagesProps) => {
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const startTimeRef = useRef<number | null>(null);
    const completedRef = useRef(false);

    useEffect(() => {
        if (!isActive) {
            setCurrentMessageIndex(0);
            startTimeRef.current = null;
            completedRef.current = false;
            return;
        }

        // Reset when starting
        if (startTimeRef.current === null) {
            startTimeRef.current = Date.now();
            setCurrentMessageIndex(0);
        }

        // Cycle through messages every 1 second
        const messageInterval = setInterval(() => {
            setCurrentMessageIndex((prev) => {
                const nextIndex = prev + 1;
                if (nextIndex >= AI_MESSAGES.length) {
                    const elapsed = Date.now() - (startTimeRef.current || 0);
                    if (elapsed >= TOTAL_DURATION && !completedRef.current) {
                        completedRef.current = true;
                        if (onComplete) {
                            setTimeout(() => onComplete(), 300);
                        }
                    }
                    return prev; // Stay on last message
                }
                return nextIndex;
            });
        }, MESSAGE_DURATION);

        // Check if all messages have been shown and minimum time has passed
        const completionCheck = setInterval(() => {
            if (startTimeRef.current && !completedRef.current) {
                const elapsed = Date.now() - startTimeRef.current;
                if (elapsed >= TOTAL_DURATION && currentMessageIndex >= AI_MESSAGES.length - 1) {
                    completedRef.current = true;
                    if (onComplete) {
                        setTimeout(() => onComplete(), 300);
                    }
                }
            }
        }, 100);

        return () => {
            clearInterval(messageInterval);
            clearInterval(completionCheck);
        };
    }, [isActive, currentMessageIndex, onComplete]);

    if (!isActive) return null;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
                gap: 3,
                animation: `${fadeIn} 0.3s ease-in-out`,
            }}
        >
            <CircularProgress
                size={32}
                thickness={4}
                sx={{
                    color: "primary.main",
                }}
            />

            <Typography
                variant="body1"
                sx={{
                    color: "text.secondary",
                    fontWeight: 400,
                    minHeight: "24px",
                }}
            >
                {AI_MESSAGES[currentMessageIndex]}
            </Typography>
        </Box>
    );
};

export default AILoadingMessages;
